import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import LayerBase from 'ol/layer/Layer';
import { useMapContext } from '../../map/MapContext';
import { useNavigation, type FocusGroupId } from '../../navigation/NavigationContext';
import {
  buildSceneTree,
  forEachLeaf,
  isLeafUnavailable,
  nodeVisibilityState,
  type SceneNode,
} from '../../map/sceneTree';
import { GEOSERVER_HOST } from '../../config/env';
import { FolderApiError } from '../../api/workspaceFolders';
import {
  BasemapLayerIcon,
  ChevronIcon,
  CircleToolIcon,
  CollapseIcon,
  EyeIcon,
  EyeMixedIcon,
  FolderIcon,
  GroupIcon,
  LineToolIcon,
  MoreIcon,
  PencilIcon,
  PhotoLayerIcon,
  PlusIcon,
  PointToolIcon,
  RasterLayerIcon,
  RefreshIcon,
  SearchIcon,
  TrashIcon,
  UnavailableIcon,
  VectorLayerIcon,
  ZoneToolIcon,
} from '../icons/Icons';
import { Tooltip } from '../Tooltip/Tooltip';
import './scene-manager.css';

function TypeIcon({ node }: { node: SceneNode }) {
  if (node.type === 'measurement') {
    switch (node.measurement.type) {
      case 'point':
        return <PointToolIcon size={13} />;
      case 'line':
        return <LineToolIcon size={13} />;
      case 'zone':
        return <ZoneToolIcon size={13} />;
      case 'circle':
        return <CircleToolIcon size={13} />;
    }
  }
  if (node.type === 'layer') {
    switch (node.entry.kind) {
      case 'raster':
        return <RasterLayerIcon />;
      case 'vector':
        return <VectorLayerIcon />;
      case 'photo':
        return <PhotoLayerIcon />;
      default:
        return <BasemapLayerIcon />;
    }
  }
  return node.type === 'group' ? <GroupIcon /> : <FolderIcon />;
}

interface RowProps {
  node: SceneNode;
  depth: number;
  collapsed: Record<string, boolean>;
  toggleCollapsed: (id: string) => void;
  query: string;
}

function matchesQuery(node: SceneNode, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  if (node.title.toLowerCase().includes(q)) return true;
  if (node.type === 'group' || node.type === 'folder') return node.children.some((c) => matchesQuery(c, query));
  return false;
}

/** Inline name box for creating or renaming a folder: Enter saves, Escape/blur-with-no-change
 * cancels. A rejected save (name taken, network) keeps the box open with the reason beneath it. */
function FolderNameEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (name: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  async function commit() {
    const name = value.trim();
    if (!name || name === initial) return onCancel();
    setSaving(true);
    try {
      await onSave(name);
    } catch (err) {
      setSaving(false);
      setError(err instanceof FolderApiError && err.status === 409 ? 'A folder with that name already exists.' : "Couldn't save the folder.");
    }
  }

  return (
    <span className="scene-folder-editor">
      <input
        ref={inputRef}
        className="scene-folder-input"
        value={value}
        maxLength={80}
        disabled={saving}
        aria-label="Folder name"
        onChange={(e) => {
          setValue(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void commit();
          if (e.key === 'Escape') onCancel();
        }}
        onBlur={() => {
          if (!saving && !error) void commit();
        }}
      />
      {error && <span className="scene-folder-error" role="alert">{error}</span>}
    </span>
  );
}

function SceneRow({ node, depth, collapsed, toggleCollapsed, query }: RowProps) {
  const {
    setLayerVisible,
    setMeasurementVisible,
    selectMeasurement,
    selectedMeasurementId,
    addFolder,
    renameFolder,
    removeFolder,
  } = useMapContext();
  // Folder editing state (hooks must run before any early return below).
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(false);
  if (!matchesQuery(node, query)) return null;

  if (node.type === 'layer') {
    const { entry } = node;
    const visible = entry.layer.getVisible();
    // Per-layer availability (MapContext -> layerHealth.ts): a layer
    // GeoServer doesn't have is still LISTED — the operator should see what
    // is configured — but marked inline, never requested, and its eye
    // can't be toggled.
    const unavailable = isLeafUnavailable(node);
    const statusLabel =
      entry.health === 'missing' ? 'not published yet' : entry.health === 'unreachable' ? 'unreachable' : null;
    const eyeDisabled = unavailable;
    const eyeReason = unavailable
      ? entry.health === 'missing'
        ? `Not published to GeoServer yet (${GEOSERVER_HOST} has no "${entry.sourceName}" layer).`
        : `GeoServer (${GEOSERVER_HOST}) isn't responding.`
      : entry.kind === 'raster'
        ? entry.userVisible === undefined
          ? `Follows the timeline bar. Click to ${visible ? 'hide' : 'show'} it regardless of date.`
          : `Set by you, regardless of the timeline date.`
        : `Toggle "${node.title}".`;
    return (
      <li className={`scene-row scene-row-layer${!visible ? ' hidden' : ''}${unavailable ? ' unavailable' : ''}`}>
        <div className="scene-row-main" style={{ paddingLeft: 30 + depth * 16 }}>
          <span className="scene-row-icon" aria-hidden="true">
            <TypeIcon node={node} />
          </span>
          <span className="scene-title">{node.title}</span>
          {statusLabel && (
            <span className="scene-row-status">
              <UnavailableIcon /> {statusLabel}
            </span>
          )}
          <Tooltip heading={unavailable ? 'Unavailable' : visible ? 'Hide layer' : 'Show layer'} description={eyeReason}>
            {/* aria-disabled + guarded onClick rather than a native `disabled`
                attribute — a disabled button doesn't reliably fire the hover
                events Tooltip needs in Chrome (same fix as the month tabs), so
                the "why can't I toggle this" explanation would never show. */}
            <button
              type="button"
              className="scene-eye"
              aria-disabled={eyeDisabled}
              aria-pressed={visible}
              aria-label={`${visible ? 'Hide' : 'Show'} ${node.title}`}
              onClick={() => {
                if (eyeDisabled) return;
                setLayerVisible(entry.id, !visible);
              }}
            >
              <EyeIcon visible={visible} />
            </button>
          </Tooltip>
        </div>
      </li>
    );
  }

  if (node.type === 'measurement') {
    const { measurement } = node;
    const visible = measurement.visible;
    const isSelected = selectedMeasurementId === measurement.id;
    return (
      <li className={`scene-row scene-row-layer scene-row-measurement${!visible ? ' hidden' : ''}${isSelected ? ' selected' : ''}`}>
        <div className="scene-row-main" style={{ paddingLeft: 30 + depth * 16 }}>
          <button
            type="button"
            className="scene-row-clickable"
            onClick={() => selectMeasurement(measurement.id)}
            aria-label={`Open details for ${measurement.name}`}
          >
            <span className="scene-row-icon" aria-hidden="true">
              <TypeIcon node={node} />
            </span>
            <span className="scene-title">{node.title}</span>
          </button>
          <Tooltip heading={visible ? 'Hide measurement' : 'Show measurement'} description={`Toggle "${node.title}" on the map.`}>
            <button
              type="button"
              className="scene-eye"
              aria-pressed={visible}
              aria-label={`${visible ? 'Hide' : 'Show'} ${node.title}`}
              onClick={() => setMeasurementVisible(measurement.id, !visible)}
            >
              <EyeIcon visible={visible} />
            </button>
          </Tooltip>
        </div>
      </li>
    );
  }

  // Tri-state, not boolean: a container whose leaves are only PARTIALLY
  // visible must not render the same fully-crossed-out eye as one that's
  // entirely hidden — that was Bug 1 (toggling one sibling made ancestor
  // groups look fully hidden even though the others were still rendering).
  const visState = nodeVisibilityState(node);
  const isOpen = !collapsed[node.id];
  // Click semantics: unless every leaf is already visible, clicking shows
  // everything; only a container that's already fully visible hides
  // everything on click. Matches common "eye" toggle conventions (e.g. a
  // mixed or fully-hidden state both resolve to "show all" on next click).
  const nextVisible = visState !== 'all';

  return (
    <li className={`scene-row scene-row-${node.type}${visState === 'none' ? ' hidden' : ''}${visState === 'mixed' ? ' mixed' : ''}`}>
      <div className="scene-row-main" style={{ paddingLeft: depth * 16 }}>
        <button
          type="button"
          className="scene-chevron-btn"
          onClick={() => toggleCollapsed(node.id)}
          aria-expanded={isOpen}
          aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.title}`}
        >
          <ChevronIcon open={isOpen} />
        </button>
        <span className="scene-row-icon" aria-hidden="true">
          <TypeIcon node={node} />
        </span>
        {renamingFolder && node.userFolderId ? (
          <FolderNameEditor
            initial={node.title}
            onSave={async (name) => {
              await renameFolder(node.userFolderId!, name);
              setRenamingFolder(false);
            }}
            onCancel={() => setRenamingFolder(false)}
          />
        ) : (
          <span className={`scene-title scene-title-${node.type}`}>{node.title}</span>
        )}
        {node.id === 'group:workspace-content' && (
          <Tooltip heading="New folder" description="Add a folder to this workspace's content.">
            <button
              type="button"
              className="scene-eye scene-action"
              aria-label="New folder"
              onClick={() => {
                setCreatingFolder(true);
                if (collapsed[node.id]) toggleCollapsed(node.id);
              }}
            >
              <PlusIcon />
            </button>
          </Tooltip>
        )}
        {node.userFolderId && !renamingFolder && (
          <>
            <Tooltip heading="Rename folder" description={`Rename "${node.title}".`}>
              <button
                type="button"
                className="scene-eye scene-action"
                aria-label={`Rename folder ${node.title}`}
                onClick={() => setRenamingFolder(true)}
              >
                <PencilIcon />
              </button>
            </Tooltip>
            <Tooltip heading="Delete folder" description="Its items move out of the folder; nothing is deleted.">
              <button
                type="button"
                className="scene-eye scene-action"
                aria-label={`Delete folder ${node.title}`}
                onClick={() => {
                  const n = node.children.length;
                  const msg = n
                    ? `Delete the folder "${node.title}"? Its ${n} item${n === 1 ? '' : 's'} will move out of the folder, not be deleted.`
                    : `Delete the empty folder "${node.title}"?`;
                  if (window.confirm(msg)) void removeFolder(node.userFolderId!).catch((e) => console.error('Failed to delete folder:', e));
                }}
              >
                <TrashIcon />
              </button>
            </Tooltip>
          </>
        )}
        <Tooltip
          heading={nextVisible ? 'Show all' : 'Hide all'}
          description={
            visState === 'mixed'
                ? `Some items in "${node.title}" are hidden.`
                : `Toggle everything in "${node.title}".`
          }
        >
          <button
            type="button"
            className="scene-eye"
            aria-pressed={visState === 'all'}
            aria-label={`${nextVisible ? 'Show' : 'Hide'} all layers in ${node.title}`}
            onClick={() =>
              forEachLeaf(node, (leaf) => {
                if (isLeafUnavailable(leaf)) return; // can't be shown — leave it alone
                if (leaf.type === 'layer') setLayerVisible(leaf.entry.id, nextVisible, { followTimeline: true });
                else setMeasurementVisible(leaf.measurement.id, nextVisible);
              })
            }
          >
            {visState === 'mixed' ? <EyeMixedIcon /> : <EyeIcon visible={visState === 'all'} />}
          </button>
        </Tooltip>
      </div>
      {isOpen && (
        <ul className="scene-children">
          {creatingFolder && (
            <li className="scene-row scene-row-folder">
              <div className="scene-row-main" style={{ paddingLeft: (depth + 1) * 16 + 30 }}>
                <FolderNameEditor
                  initial=""
                  onSave={async (name) => {
                    await addFolder(name);
                    setCreatingFolder(false);
                  }}
                  onCancel={() => setCreatingFolder(false)}
                />
              </div>
            </li>
          )}
          {node.id === 'group:workspace-content' && node.children.length === 0 && !creatingFolder && (
            <li className="scene-empty-hint" style={{ paddingLeft: (depth + 1) * 16 + 30 }}>
              Nothing here yet — draw a measurement, or add a folder with +.
            </li>
          )}
          {node.children.map((child) => (
            <SceneRow
              key={child.id}
              node={child}
              depth={depth + 1}
              collapsed={collapsed}
              toggleCollapsed={toggleCollapsed}
              query={query}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Left "Scene Manager" panel — row/header/search layout modeled on a
 * reference UI/UX pattern the user shared (layout inspiration only; no
 * borrowed branding, icon set is this app's own generic glyphs — see
 * components/icons/Icons.tsx). Real 3-level Group -> Folder -> Layer tree
 * (`sceneTree.ts` builds it from the same layer state the map itself uses).
 */
const MIN_WIDTH = 200;
const MAX_WIDTH = 500;
const DEFAULT_WIDTH = 280;

// Every top-level Scene Manager group id — used to collapse "everything
// except the one the current workspace cares about".
const TOP_GROUP_IDS = ['group:workspace-content', 'group:geoserver', 'group:field-photos', 'group:basemap'];

/** Maps a workspace card's landing focus to a Scene Manager collapse
 * state. 'general' is the real, working baseline (today's map as it
 * already is) — its "focus" is just the fully-expanded default, nothing to
 * hide. 'field-photos' collapses everything else and opens Field Photos.
 * 'logistics'/'crop-growth' have no Scene Manager target at all (no data
 * exists for either domain yet — see MapView.tsx's empty-state banner
 * instead), so this returns null and the tree's current state is left
 * alone rather than forcing a change that wouldn't mean anything. */
function collapseStateFor(focus: FocusGroupId): Record<string, boolean> | null {
  if (focus === 'general') return {};
  if (focus === 'field-photos') {
    const next: Record<string, boolean> = {};
    TOP_GROUP_IDS.forEach((id) => {
      next[id] = id !== 'group:field-photos';
    });
    return next;
  }
  return null;
}

export function SceneManager() {
  // visibilityTick isn't used directly — reading it subscribes this panel to
  // the timeline effect's direct OL-layer visibility changes, so rows that
  // read layer.getVisible() during render stay current (see MapContext).
  const { map, layers, compareMode, measurements, folders, workspaceSlug, recheckLayerHealth, visibilityTick } =
    useMapContext();
  void visibilityTick;
  const { focusGroupId, navToken } = useNavigation();
  const [collapsedPanel, setCollapsedPanel] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState('');
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const draggingRef = useRef(false);
  const tree = useMemo(
    () => buildSceneTree(layers, measurements, folders, workspaceSlug),
    [layers, measurements, folders, workspaceSlug],
  );

  // Re-applies the landing focus every time a workspace card is opened
  // (navToken bumps on every goToMap() call, even re-selecting the same
  // workspace) — not just the first time a given focus id is seen, so
  // browsing away and back always lands in the expected state.
  useEffect(() => {
    const next = collapseStateFor(focusGroupId);
    if (next) setCollapsed(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navToken]);

  function toggleCollapsed(id: string) {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  // Drag-to-resize on the panel's right edge — plain in-memory state, no
  // persistence needed. Only active while expanded; the collapse button
  // still snaps straight to the thin strip independently of whatever width
  // was last dragged to.
  function onResizeStart(e: ReactPointerEvent) {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startWidth = width;

    function onMove(evt: PointerEvent) {
      if (!draggingRef.current) return;
      const next = startWidth + (evt.clientX - startX);
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, next)));
    }
    function onUp() {
      draggingRef.current = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  // Trivial, genuinely functional refresh: re-runs the per-layer
  // availability check (so a raster published since page load — or a
  // GeoServer that has come back — is picked up without a reload) and
  // re-issues every visible layer source's own load, rather than just
  // spinning an icon for show.
  function refreshAll() {
    recheckLayerHealth();
    map.getLayers().forEach((l) => {
      if (l instanceof LayerBase && l.getVisible()) l.getSource()?.refresh();
    });
  }

  if (collapsedPanel) {
    return (
      <aside className="scene-manager scene-manager-collapsed">
        <Tooltip heading="Expand" description="Show the Scene Manager panel.">
          <button type="button" className="scene-manager-expand-btn" onClick={() => setCollapsedPanel(false)}>
            <CollapseIcon collapsed />
          </button>
        </Tooltip>
      </aside>
    );
  }

  return (
    <aside className="scene-manager" style={{ width }}>
      <div className="scene-manager-inner">
        <div className="scene-manager-header">
          <span className="scene-manager-title">Scene Manager</span>
          <div className="scene-manager-header-actions">
            <Tooltip heading="Refresh" description="Reload every layer's data.">
              <button type="button" className="scene-manager-icon-btn" onClick={refreshAll} aria-label="Refresh layers">
                <RefreshIcon />
              </button>
            </Tooltip>
            <Tooltip heading="More" description="Not available yet.">
              <button type="button" className="scene-manager-icon-btn" aria-label="More options">
                <MoreIcon />
              </button>
            </Tooltip>
            <Tooltip heading="Collapse" description="Shrink the Scene Manager to a thin strip.">
              <button
                type="button"
                className="scene-manager-icon-btn"
                onClick={() => setCollapsedPanel(true)}
                aria-label="Collapse Scene Manager"
              >
                <CollapseIcon />
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="scene-manager-search">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search layers"
          />
        </div>

        <ul className="scene-tree">
          {tree.map((node) => (
            <SceneRow key={node.id} node={node} depth={0} collapsed={collapsed} toggleCollapsed={toggleCollapsed} query={query} />
          ))}
        </ul>

        <p className="scene-manager-hint">
          Rasters follow the timeline bar until you click their eye &mdash; your choice then stays, whatever the date.
        </p>

        <Tooltip heading="Add content" description="Not available yet.">
          <button type="button" className="scene-manager-add-content">
            <PlusIcon /> Add content
          </button>
        </Tooltip>
      </div>

      <div
        className="scene-manager-resize-handle"
        onPointerDown={onResizeStart}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Scene Manager panel"
      />
    </aside>
  );
}
